// When an asset's evidence was carried forward without being re-read.
//
// A provider read that fails (an OpenAI project key that is refused, times out, or cannot be
// read) does not expire the agents behind it: discovery re-emits their stored signal flagged
// `notReReadThisCycle`, with `lastConfirmedAt` = the last time the provider actually returned it.
// Re-emitting bumps the signal's (and so the asset's) lastSeenAt to the latest scan, so showing
// "Last seen" alone would overstate how fresh that evidence is.

/** The time this signal was last genuinely read, or null when it was read this cycle. */
export function signalNotReReadSince(signal) {
  if (!signal || signal.status === "Gone") return null;
  const evidence = signal.evidence;
  if (!evidence || evidence.notReReadThisCycle !== true) return null;
  // Flagged but with no usable timestamp: still not re-read, the time is just unknown.
  const at = typeof evidence.lastConfirmedAt === "string" && !Number.isNaN(Date.parse(evidence.lastConfirmedAt))
    ? evidence.lastConfirmedAt
    : null;
  return { lastConfirmedAt: at };
}

const time = (s) => {
  const t = typeof s === "string" ? Date.parse(s) : NaN;
  return Number.isNaN(t) ? null : t;
};

const latest = (values) => values.reduce((a, b) => (b !== null && (a === null || b > a) ? b : a), null);

/**
 * Whether the asset's "Last seen" comes from carried-forward evidence rather than a real read.
 * The asset's last-seen time is the latest of its live (non-Gone) signals, so it is overstated
 * exactly when the most recently "seen" signal was not re-read: no genuinely read signal is as
 * recent. Returns { lastConfirmedAt } — the latest genuine observation across all live signals
 * (a read signal's lastSeenAt, a carried-forward one's lastConfirmedAt), as an ISO string, or null
 * when unknown — or null when the asset's last-seen time is a real read.
 */
export function assetNotReReadSince(signals) {
  const live = (signals || []).filter((s) => s && s.status !== "Gone");
  const flagged = live.filter((s) => signalNotReReadSince(s) !== null);
  if (flagged.length === 0) return null;
  const read = live.filter((s) => signalNotReReadSince(s) === null);

  const latestRead = latest(read.map((s) => time(s.lastSeenAt)));
  const latestCarried = latest(flagged.map((s) => time(s.lastSeenAt)));
  // A genuinely read signal at least as recent as the carried-forward ones: last seen is real.
  if (latestRead !== null && (latestCarried === null || latestRead >= latestCarried)) return null;

  const confirmed = latest([
    latestRead,
    ...flagged.map((s) => time(signalNotReReadSince(s).lastConfirmedAt)),
  ]);
  return { lastConfirmedAt: confirmed === null ? null : new Date(confirmed).toISOString() };
}
